import { RestRequest, RestContext, ResponseFunction } from 'msw'
import {
  addToEntityListData,
  addNewEntityDataToEntityListData,
  updateEntityListData,
  updateEntityListDataMultiple,
  updateEntityListDataMultipleFromUpdatedEntityData,
  deleteEntityListData,
  deleteEntityListDataMultiple,
} from '../../modules/mockDataCommon'
import {
  findVersionConflict,
  versionConflictBody,
} from '../../modules/versionConflict'
import { requireAuthenticatedUser } from '../../decorators'
import { withSupplierProductAndItemGuard } from '../../modules/supplierProductAndItemConflict'
import { createMockOperation } from '../operations'
import {
  getOpportunityMSServiceListData,
  getCompanyDataByOpportunityMSServiceId,
  propagateOpportunityMSServiceOrServicesAdditions,
  propagateOpportunityMSServicesChanges,
  propagateOpportunityMSServiceChanges,
  propagateOpportunityMSServiceDeletions,
} from './mockData'
import type { ICompany } from '~/types/company/models'

// async function getOpportunityMSServices(
//   req: RestRequest,
//   res: ResponseFunction,
//   ctx: RestContext
// ) {
//   console.log(`'Returning opportunitymasterservices.`)
//   return res(ctx.status(200), ctx.json({ items: getOpportunityMSServiceListData() }))
// }

interface GetCompanyFromOpportunityMSServiceResponse {
  data: ICompany
}

async function getCompanyFromOpportunityMSService(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const responseBody: GetCompanyFromOpportunityMSServiceResponse = {
      data: await getCompanyDataByOpportunityMSServiceId(id), // will either be valid or will have thrown error
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e: unknown) {
    const responseBody = {
      statusCode: 404,
      message: `Can't get Company from opportunityMSService ${id}: ${e.message}`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

// post

const postOpportunityMSService = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const idempotencyKey = req.headers.get('Idempotency-Key')
  if (idempotencyKey) {
    const newEntityData = await req.json()
    const data = await addNewEntityDataToEntityListData({
      req,
      newEntityData,
      getEntityListDataFn: getOpportunityMSServiceListData,
      postProcessOnceForAllFn: propagateOpportunityMSServiceOrServicesAdditions,
    })
    const operationId = createMockOperation(
      'POST /api/opportunitymsservices',
      data
    )
    return res(
      ctx.status(202),
      ctx.json({
        data: {
          operation_id: operationId,
          status: 'pending',
          operation_type: 'POST /api/opportunitymsservices',
        },
      })
    )
  }
  return addToEntityListData(
    req,
    res,
    ctx,
    getOpportunityMSServiceListData,
    undefined,
    propagateOpportunityMSServiceOrServicesAdditions
  )
}

const patchOpportunityMSService = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return updateEntityListData(req, res, ctx, getOpportunityMSServiceListData, [
    propagateOpportunityMSServiceChanges,
  ])
}

const patchOpportunityMSServices = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const idempotencyKey = req.headers.get('Idempotency-Key')
  if (idempotencyKey) {
    const updatedEntityDataMultiple = await req.json()
    // ⚠ THE VERSION CHECK HAS TO BE HERE TOO, and its absence is why every
    // multi-row msService PATCH in the mock accepted any version at all: the
    // check lives in `updateEntityListDataMultiple`, this branch calls the
    // underlying helper directly, and `update*MSService` always sets
    // `doAsync: true` for an array -- so this is the ONLY branch these requests
    // ever take. The 409 class the mock was fixed to reproduce was unreachable
    // on the one path the app actually uses.
    const conflict = findVersionConflict({
      listData: await getOpportunityMSServiceListData(),
      submittedRows: updatedEntityDataMultiple,
    })
    if (conflict) {
      return res(ctx.status(409), ctx.json(versionConflictBody(conflict)))
    }
    const data = await updateEntityListDataMultipleFromUpdatedEntityData({
      req,
      updatedEntityDataMultiple,
      getEntityListDataFn: getOpportunityMSServiceListData,
      postProcessOnceForAllFn: propagateOpportunityMSServicesChanges,
    })
    const operationId = createMockOperation(
      'PATCH /api/opportunitymsservices',
      data
    )
    return res(
      ctx.status(202),
      ctx.json({
        data: {
          operation_id: operationId,
          status: 'pending',
          operation_type: 'PATCH /api/opportunitymsservices',
        },
      })
    )
  }
  return updateEntityListDataMultiple(
    req,
    res,
    ctx,
    getOpportunityMSServiceListData,
    undefined,
    propagateOpportunityMSServicesChanges
  )
}

const deleteOpportunityMSService = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListData(
    req,
    res,
    ctx,
    getOpportunityMSServiceListData,
    undefined,
    propagateOpportunityMSServiceDeletions
  )
}

const deleteOpportunityMSServices = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListDataMultiple(
    req,
    res,
    ctx,
    getOpportunityMSServiceListData,
    undefined,
    propagateOpportunityMSServiceDeletions // 'Service' is correctly singular here
  )
}

export const handlers = [
  {
    method: 'get',
    path: '/api/opportunitymsservices/:id/company',
    handler: getCompanyFromOpportunityMSService,
  },
  {
    method: 'post',
    path: '/api/opportunitymsservices',
    handler: withSupplierProductAndItemGuard(postOpportunityMSService),
  },
  {
    method: 'patch',
    path: '/api/opportunitymsservices/:id',
    handler: withSupplierProductAndItemGuard(
      patchOpportunityMSService,
      getOpportunityMSServiceListData
    ),
  },
  {
    method: 'patch',
    path: '/api/opportunitymsservices',
    handler: withSupplierProductAndItemGuard(
      patchOpportunityMSServices,
      getOpportunityMSServiceListData
    ),
  },
  {
    method: 'delete',
    path: '/api/opportunitymsservices',
    handler: deleteOpportunityMSServices,
  },
  {
    method: 'delete',
    path: '/api/opportunitymsservices/:id',
    handler: deleteOpportunityMSService,
  },
].map((h) => ({ ...h, ...{ handler: requireAuthenticatedUser(h.handler) } }))
